import { ContentSurfaceWidget } from '../../widgets/ContentSurfaceWidget';
import { Gallery, GalleryItem } from '../../widgets/DocsGalleryWidget';
import styled from '@emotion/styled';
import { DocsCodeBlockWidget } from '../../widgets/DocsCodeBlockWidget';
import { DOCS_REDIRECTS } from '@journeyapps/package-types';
import * as React from 'react';
import { useLayoutEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { BREAKPOINT, DESKTOP_HEADER_PADDING, IPAD_MEDIA, PAGE_LAYOUT_GAP, PAGE_PADDING } from '../../vars';
import { AttributeReferenceWidget } from '../../widgets/AttributeReferenceWidget';
import { CrumbWidget } from '../../widgets/CrumbWidget';
import { ContentWidget } from '../../widgets/ContentWidget';
import { DocsCard, DocsCardGrid } from '../../widgets/DocsCardWidget';
import { DocsReferenceWidget } from '../../widgets/DocsReferenceWidget';
import { DocsCalloutWidget, Enterprise, Info, Note, Tip, Warning } from '../../widgets/DocsCalloutWidget';
import { LanguageTab, LanguageTabs } from '../../widgets/DocsLanguageTabsWidget';
import { InlineBadgeLabelWidget } from '../../widgets/InlineBadgeLabelWidget';
import { NavigationTreeWidget } from '../../widgets/NavigationTreeWidget';
import { SyntaxCardWidget } from '../../widgets/SyntaxCardWidget';
import { TagBadgeWidget } from '../../widgets/TagBadgeWidget';
import { ContainerVersionWidget, RuntimeVersionWidget } from '../../widgets/VersionCompatibilityWidget';
import { TableOfContentsWidget } from '../../widgets/TableOfContentsWidget';
import { LayoutClassName } from '../../widgets/layout/LayoutClassName';
import { PageWidget } from '../../widgets/layout/PageWidget';
import { ReadingLayoutWidget } from '../../widgets/layout/ReadingLayoutWidget';
import { InlineLink } from '../shared/InlineLink';
import { MdxLink } from '../shared/MdxLink';
import { MdxTable } from '../shared/MdxTable';
import { NotFoundPage } from '../NotFoundPage';
import {
  buildDocsNavigationTree,
  DOCS_NAVIGATION,
  findDocsPage,
  getDocsBreadcrumbParts,
  normalizePermalink
} from './shared/docs';

const SIDEBAR_WIDTH = 310;
const CONTENT_WIDTH = 1120;

export const DocsArticlePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLElement>(null);
  const requestedPermalink = normalizePermalink(location.pathname);
  const redirectTarget = DOCS_REDIRECTS[requestedPermalink];

  const found = useMemo(() => findDocsPage(requestedPermalink), [requestedPermalink]);
  const navigation = useMemo(() => buildDocsNavigationTree(requestedPermalink), [requestedPermalink]);
  const breadcrumbParts = useMemo(() => getDocsBreadcrumbParts(requestedPermalink), [requestedPermalink]);
  const mdxComponents = useMemo(
    () => ({
      Callout: DocsCalloutWidget,
      AttributeReference: AttributeReferenceWidget,
      Card: DocsCard,
      CardGrid: DocsCardGrid,
      DocsReference: DocsReferenceWidget,
      Enterprise,
      Info,
      InlineBadgeLabel: InlineBadgeLabelWidget,
      InlineLink,
      LanguageTab,
      LanguageTabs,
      Note,
      SyntaxCard: SyntaxCardWidget,
      TagBadgeWidget,
      Tip,
      RuntimeVersion: RuntimeVersionWidget,
      ContainerVersion: ContainerVersionWidget,
      Warning,
      Gallery,
      GalleryItem,
      pre: DocsCodeBlockWidget,
      table: MdxTable,
      a: MdxLink
    }),
    []
  );

  useLayoutEffect(() => {
    scrollContainerRef.current = document.querySelector<HTMLElement>(`.${LayoutClassName.SCROLL_CONTAINER}`);
  }, []);

  React.useEffect(() => {
    if (redirectTarget) {
      navigate(`${redirectTarget}${location.search}${location.hash}`, { replace: true });
    }
  }, [redirectTarget, location.search, location.hash, navigate]);

  if (redirectTarget) {
    return null;
  }

  if (!found) {
    return <NotFoundPage />;
  }

  const DocContent = found.default as React.ComponentType<{ components?: Record<string, React.ComponentType<any>> }>;
  const rawMarkdownHref = `/dist/raw-docs${requestedPermalink.replace(/^\/docs/, '')}index.mdx`;

  return (
    <PageWidget title={found.frontmatter.title} center={false} topPadding={PAGE_LAYOUT_GAP}>
      <S.PageInset>
        <ReadingLayoutWidget
          navigation={<NavigationTreeWidget title={DOCS_NAVIGATION.title} items={navigation} />}
          tableOfContents={
            found.tableOfContents?.length ? (
              <TableOfContentsWidget
                key={requestedPermalink}
                items={found.tableOfContents}
                contentRef={contentRef}
                scrollContainerRef={scrollContainerRef}
              />
            ) : undefined
          }
          sidebarWidth={SIDEBAR_WIDTH}
          compactSidebarWidth={260}
          contentWidth={CONTENT_WIDTH}
        >
          <S.Content ref={contentRef}>
            <S.TopBar>
              {breadcrumbParts.length > 0 ? (
                <S.Crumb>
                  <CrumbWidget parts={breadcrumbParts} lastPartIsCurrentPage={false} />
                </S.Crumb>
              ) : null}
              <S.RawMarkdownLink href={rawMarkdownHref} target="_blank" rel="noreferrer">
                View Raw Markdown
              </S.RawMarkdownLink>
            </S.TopBar>
            <S.Divider />
            <ContentWidget variant="article">
              <DocContent components={mdxComponents} />
            </ContentWidget>
          </S.Content>
        </ReadingLayoutWidget>
      </S.PageInset>
    </PageWidget>
  );
};

namespace S {
  const WIDE_LAYOUT_WIDTH = 1888;
  const WIDE_LAYOUT_VIEWPORT_GUTTER = DESKTOP_HEADER_PADDING * 2;

  export const PageInset = styled.div`
    margin-left: auto;
    margin-right: auto;
    width: min(calc(100vw - ${WIDE_LAYOUT_VIEWPORT_GUTTER}px), ${WIDE_LAYOUT_WIDTH}px);

    @media (max-width: ${BREAKPOINT.DOCS_SIDEBARS}px) {
      margin-left: ${PAGE_PADDING}px;
      margin-right: ${PAGE_PADDING}px;
      width: auto;
    }
  `;

  export const Crumb = styled.div`
    --breadcrumb-gap: 8px;
    --breadcrumb-separator-size: 9px;
    font-size: 13px;
    line-height: 1.5;
    min-width: 0;
  `;

  export const TopBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;

    @media (max-width: ${IPAD_MEDIA}px) {
      align-items: flex-start;
      flex-direction: column;
    }
  `;

  export const Divider = styled.hr`
    border: 0;
    border-top: 1px solid ${(p) => p.theme.text.listBorder};
    margin: 20px 0;
  `;

  export const RawMarkdownLink = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 30px;
    padding: 0 12px;
    border-radius: 999px;
    border: 1px solid ${(p) => p.theme.text.listBorder};
    background: ${(p) => p.theme.text.listBackground};
    color: ${(p) => p.theme.text.heading};
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.03em;
    text-decoration: none;
    white-space: nowrap;

    &:hover {
      transition:
        background 0.2s ease,
        border-color 0.2s ease;
      background: ${(p) => (p.theme.light ? 'rgb(238 238 246)' : 'rgba(143, 89, 255, 0.18)')};
      border-color: ${(p) => (p.theme.light ? 'rgba(143, 89, 255, 0.28)' : 'rgba(143, 89, 255, 0.28)')};
      text-decoration: none;
    }
  `;

  export const Content = styled(ContentSurfaceWidget)`
    max-width: ${CONTENT_WIDTH}px;
  `;
}
